import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Badge,
  Menu as NotificationMenu,
  MenuList,
  Divider,
  CircularProgress,
  Typography as MuiTypography,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  // Dashboard as DashboardIcon, // Zakomentarisano - Kontrolna tabla je sakrivena
  Business as BusinessIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Build as BuildIcon,
  Settings as SettingsIcon,
  EventBusy as EventBusyIcon,
  Logout as LogoutIcon,
  Policy as PolicyIcon,
  MailOutline as MailOutlineIcon,
  CalendarMonth as CalendarIcon,
  Work as WorkIcon,
  Menu as MenuIcon,
  Add as AddIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  MoreHoriz as MoreHorizIcon,
  HourglassEmpty as HourglassEmptyIcon,
  AccountCircle as AccountCircleIcon,
  Notifications as NotificationsIcon,
  Tv as LiveDashboardIcon,
  BarChart as BarChartIcon,
  //Science as ScienceIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { setCurrentPage } from "../store/slices/uiSlice";
import { logout, updateUser } from "../store/slices/authSlice";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import tenantService from "../services/tenantService";
import authService from "../services/authService";
import notificationService from "../services/notificationService";
import { isSuperAdmin, isTenantAdmin, isEmployee, isCustomer } from "../utils/userHelper";
import { getTenantSubdomain } from "../utils/subdomain";
import { toast } from 'react-toastify';
import { isDemoMode } from "../utils/demoMode";
import NotificationItem from './NotificationItem';
import tenantUsersService from "../services/tenantUsersService";
import appointmentService from "../services/appointmentService";
import Logo from "../assets/ZakazujMeLogo4.png";

const expandedWidth = 260;
const collapsedWidth = 72;

const normalizeTenantId = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      return value._id || value.id || value.tenantId || null;
    }
    return null;
  };

const extractNotificationTenantId = (notification) => {
  if (!notification || typeof notification !== 'object') return null;

  const candidates = [
    notification.tenantId,
    notification.tenant?.tenantId,
    notification.tenant?._id,
    notification.tenant?.id,
    notification.tenant,
    notification.data?.tenantId,
    notification.details?.tenantId,
    notification.metadata?.tenantId,
    notification.meta?.tenantId,
    notification.payload?.tenantId,
    notification.context?.tenantId,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeTenantId(candidate);
    if (normalized) return normalized;
  }

  return null;
};

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // const { sidebarOpen } = useSelector((state) => state.ui);
  const { user } = useSelector((state) => state.auth);
  const { isDarkMode, toggleTheme } = useAppTheme();

  const [collapsed, setCollapsed] = useState(isMobile); // Po defaultu minimiziran na mobilnim
  const [expandedMenus, setExpandedMenus] = useState(new Set());
  const [mobileOpen, setMobileOpen] = useState(false); // State za mobilni sidebar
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false); // State za desni mini sidebar
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [currentTenantName, setCurrentTenantName] = useState("");
  const [tenantNameFromId, setTenantNameFromId] = useState("");
  const [previousTenantId, setPreviousTenantId] = useState("");
  const [tenantLogoUrl, setTenantLogoUrl] = useState("");
  const [tenantSettings, setTenantSettings] = useState(null);
  
  // Account menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  
  // Profile modal state
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phone: user?.phone || "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Notification state
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [allNotifications, setAllNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const notificationMenuOpen = Boolean(notificationAnchor);

  const tenantSubdomain = useMemo(() => getTenantSubdomain(), []);
  const isTenantSpecificMode = Boolean(tenantSubdomain);
  const isCustomerUser = useMemo(() => isCustomer(user, selectedTenantId), [user, selectedTenantId]);
  const isTenantAdminUser = useMemo(() => isTenantAdmin(user, selectedTenantId), [user, selectedTenantId]);
  const isEmployeeUser = useMemo(() => isEmployee(user, selectedTenantId), [user, selectedTenantId]);

  const activeTenantId = useMemo(() => {
    if (!user) return null;

    const userTenantId = normalizeTenantId(user.tenantId);
    if (userTenantId) return userTenantId;

    if (selectedTenantId) return selectedTenantId;

    if (user?.tenantConnections?.length) {
      const connection = selectedTenantId
        ? user.tenantConnections.find((tc) => {
            const tcTenantId = normalizeTenantId(tc.tenantId);
            return tcTenantId === selectedTenantId;
          }) || user.tenantConnections[0]
        : user.tenantConnections[0];

      return normalizeTenantId(connection?.tenantId);
    }

    return null;
  }, [user, selectedTenantId]);

  const isHomepage = location.pathname === '/homepage';
  const shouldFilterNotifications = !isHomepage && Boolean(activeTenantId);

  const filteredNotifications = useMemo(() => {
    if (!shouldFilterNotifications || !activeTenantId) {
      return allNotifications;
    }

    return allNotifications.filter((notification) => {
      const notificationTenantId = extractNotificationTenantId(notification);
      return notificationTenantId
        ? notificationTenantId === activeTenantId
        : false;
    });
  }, [allNotifications, shouldFilterNotifications, activeTenantId]);
  
  // State for header minimization on scroll
  const [headerMinimized, setHeaderMinimized] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // State for fullscreen detection
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Ažuriraj collapsed state kada se promeni veličina ekrana
  useEffect(() => {
    setCollapsed(isMobile);
  }, [isMobile]);

  // Detektuj fullscreen mod (F11 i Fullscreen API) - samo na live-dashboard stranici
  useEffect(() => {
    // Ako nisi na live-dashboard stranici, resetuj fullscreen state
    if (location.pathname !== '/live-dashboard') {
      setIsFullscreen(false);
      // Ne brišemo localStorage flag ovde - LiveDashboard će to uraditi kada se unmount-uje
      return;
    }

    const checkFullscreen = () => {
      // Ako nisi na live-dashboard, ne proveravaj fullscreen
      if (location.pathname !== '/live-dashboard') {
        setIsFullscreen(false);
        localStorage.removeItem('hideSidebarNavbar');
        return;
      }

      // Proveri Fullscreen API prvo
      const isFullscreenAPI = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );

      if (isFullscreenAPI) {
        setIsFullscreen(true);
        localStorage.setItem('hideSidebarNavbar', 'true');
        return;
      }

      // Ako nije Fullscreen API, proveri localStorage flag koji postavlja LiveDashboard komponenta
      // Ne proveravamo dimenzije jer su nepouzdane - verujemo LiveDashboard komponenti
      const localStorageFlag = localStorage.getItem('hideSidebarNavbar') === 'true';
      setIsFullscreen(localStorageFlag);
    };

    const handleFullscreenChange = () => {
      checkFullscreen();
    };

    // Ne dodajemo resize listener - LiveDashboard komponenta će to raditi
    // Ne dodajemo keyboard listener - LiveDashboard komponenta će to raditi

    // Dodaj event listeners SAMO za Fullscreen API
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Resetuj na početku kada se promeni lokacija na live-dashboard
    // Ovo osigurava da sidebar/navbar budu vidljivi po defaultu
    setIsFullscreen(false);
    localStorage.removeItem('hideSidebarNavbar');
    
    // Ne pozivamo checkFullscreen na početku - samo će se pozivati na resize, fullscreen events i custom events

    // Proveri custom event za promene fullscreen stanja
    const handleFullscreenToggle = () => {
      checkFullscreen();
    };
    window.addEventListener('fullscreenToggle', handleFullscreenToggle);
    
    // Ne proveravamo periodično - samo na fullscreen events i custom events
    // Ovo sprečava da se sidebar/navbar sakriju kada nije u fullscreen

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      window.removeEventListener('fullscreenToggle', handleFullscreenToggle);
      // interval je null, nema potrebe za cleanup
      if (window.fullscreenCheckTimeout) {
        clearTimeout(window.fullscreenCheckTimeout);
      }
    };
  }, [location.pathname]);

  // Ažuriraj selectedTenantId kada se promeni u window objektu
  useEffect(() => {
    const checkSelectedTenant = () => {
      if (window.selectedTenantId !== selectedTenantId) {
        setSelectedTenantId(window.selectedTenantId || "");
      }
    };

    const interval = setInterval(checkSelectedTenant, 100);
    return () => clearInterval(interval);
  }, [selectedTenantId]);

  // Ažuriraj ime tenanta i logo kada se promeni user ili tenantId
  useEffect(() => {
    console.log('🔍 Layout useEffect triggered:', {
      userType: user?.userType,
      tenantId: user?.tenantId,
      previousTenantId,
      tenantConnectionsLength: user?.tenantConnections?.length,
      currentTenantName,
      tenantIdType: typeof user?.tenantId,
      previousTenantIdType: typeof previousTenantId,
      areEqual: user?.tenantId === previousTenantId
    });
    
    // Proveri da li je korisnik customer, employee ili tenant_admin
    const isCustomerCheck = isCustomerUser;
    const isEmployeeCheck = isEmployeeUser;
    const isTenantAdminCheck = isTenantAdminUser;
    
    // Funkcija za učitavanje tenant podataka (ime, logo i settings)
    const fetchTenantData = async (tenantId) => {
      try {
        const tenant = await tenantService.getTenantById(tenantId);
        console.log('✅ Layout - Fetched tenant:', tenant);
        setTenantNameFromId(tenant?.name || 'Tenant');
        setTenantLogoUrl(tenant?.logoUrl || '');
        setTenantSettings(tenant?.settings || null);
        setPreviousTenantId(tenantId);
      } catch (error) {
        console.error('Error fetching tenant data:', error);
        setTenantNameFromId('Tenant');
        setTenantLogoUrl('');
        setTenantSettings(null);
        setPreviousTenantId(tenantId);
      }
    };
    
    if (isCustomerCheck || isEmployeeCheck || isTenantAdminCheck) {
      // Odredi koji tenant ID koristiti i prvo proveri tenantConnections za logo
      let activeTenantId = null;
      let tenantDataFromConnection = null;
      
      if (isTenantAdminCheck || isEmployeeCheck) {
        // Za tenant_admin i employee, koristi tenantId iz user objekta ili selectedTenantId
        activeTenantId = user?.tenantId || selectedTenantId || (user?.tenantConnections?.[0]?.tenantId?._id || user?.tenantConnections?.[0]?.tenantId);
        
        // Proveri da li ima tenant podatke u tenantConnections
        if (user?.tenantConnections && user.tenantConnections.length > 0) {
          const connection = selectedTenantId 
            ? user.tenantConnections.find(tc => {
                const tcTenantId = tc.tenantId?._id || tc.tenantId;
                return tcTenantId === selectedTenantId;
              })
            : user.tenantConnections[0];
          
          if (connection?.tenantId) {
            tenantDataFromConnection = {
              name: typeof connection.tenantId === 'object' ? connection.tenantId.name : undefined,
              logoUrl: typeof connection.tenantId === 'object' ? connection.tenantId.logoUrl : undefined,
              settings: typeof connection.tenantId === 'object' ? connection.tenantId.settings : undefined,
              tenantId: connection.tenantId._id || connection.tenantId
            };
          }
        }
      } else if (isCustomerCheck) {
        // Za customer, prioritet je user.tenantId (iz homepage klik), pa tenantConnections
        if (user?.tenantId) {
          activeTenantId = user.tenantId;
          
          // Proveri tenantConnections za podatke
          if (user?.tenantConnections && user.tenantConnections.length > 0) {
            const connection = user.tenantConnections.find(tc => {
              const tcTenantId = tc.tenantId?._id || tc.tenantId;
              return tcTenantId === user.tenantId;
            }) || user.tenantConnections[0];
            
            if (connection?.tenantId) {
              tenantDataFromConnection = {
                name: typeof connection.tenantId === 'object' ? connection.tenantId.name : undefined,
                logoUrl: typeof connection.tenantId === 'object' ? connection.tenantId.logoUrl : undefined,
                settings: typeof connection.tenantId === 'object' ? connection.tenantId.settings : undefined,
                tenantId: connection.tenantId._id || connection.tenantId
              };
            }
          }
        } else if (user?.tenantConnections?.length === 1) {
          activeTenantId = user.tenantConnections[0].tenantId?._id || user.tenantConnections[0].tenantId;
          const connection = user.tenantConnections[0];
          if (connection?.tenantId) {
            tenantDataFromConnection = {
              name: typeof connection.tenantId === 'object' ? connection.tenantId.name : undefined,
              logoUrl: typeof connection.tenantId === 'object' ? connection.tenantId.logoUrl : undefined,
              tenantId: connection.tenantId._id || connection.tenantId
            };
          }
        }
      }
      
      if (activeTenantId && activeTenantId !== previousTenantId) {
        // Ako imamo podatke iz tenantConnections (uključujući logo), koristi ih
        if (tenantDataFromConnection && tenantDataFromConnection.logoUrl) {
          console.log('📌 Layout - Using tenant data from connection:', tenantDataFromConnection);
          setTenantNameFromId(tenantDataFromConnection.name || 'Tenant');
          setTenantLogoUrl(tenantDataFromConnection.logoUrl);
          setTenantSettings(tenantDataFromConnection.settings || null);
          setPreviousTenantId(tenantDataFromConnection.tenantId);
          
          if (isCustomerCheck && user?.tenantId) {
            setCurrentTenantName(tenantDataFromConnection.name || 'Tenant');
          }
        } else {
          // Učitaj tenant podatke (ime i logo) preko API-ja
          console.log('🔄 Layout - Fetching tenant data for tenantId:', activeTenantId);
          fetchTenantData(activeTenantId);
        }
      } else if (isCustomerCheck && user?.tenantConnections?.length === 1 && !user?.tenantId && tenantDataFromConnection) {
        // FALLBACK za customer: Koristi tenantConnections samo ako nema tenantId
        console.log('📌 Layout - Single connection (fallback), setting tenant data:', tenantDataFromConnection.name);
        setCurrentTenantName(tenantDataFromConnection.name || 'Tenant');
        setTenantLogoUrl(tenantDataFromConnection.logoUrl || '');
        setTenantSettings(tenantDataFromConnection.settings || null);
        setPreviousTenantId(tenantDataFromConnection.tenantId);
      } else if (!activeTenantId) {
        console.log('🧹 Layout - No tenantId, clearing tenant data');
        setCurrentTenantName("");
        setTenantLogoUrl("");
        setTenantSettings(null);
        setPreviousTenantId("");
      }
    } else {
      console.log('👤 Layout - Not a customer/employee/admin, clearing tenant data');
      setCurrentTenantName("");
      setTenantLogoUrl("");
      setTenantSettings(null);
      setPreviousTenantId("");
    }
  }, [user?.tenantId, previousTenantId, user?.userType, user?.tenantConnections, currentTenantName, selectedTenantId, user, isCustomerUser, isEmployeeUser, isTenantAdminUser]);

  // Ažuriraj currentTenantName kada se promeni tenantNameFromId
  useEffect(() => {
    const isCustomerCheck = isCustomerUser;
    
    console.log('🔄 Layout useEffect (tenantNameFromId):', {
      userType: user?.userType,
      tenantId: user?.tenantId,
      tenantNameFromId,
      isCustomerCheck,
      willUpdate: !!(isCustomerCheck && user?.tenantId && tenantNameFromId)
    });
    
    if (isCustomerCheck && user?.tenantId && tenantNameFromId) {
      console.log('✅ Setting currentTenantName to:', tenantNameFromId);
      setCurrentTenantName(tenantNameFromId);
      
      // Ako već nismo učitavali logo (iz tenantConnections), proveri da li treba da učitaš logo preko API-ja
      // Logo se već učitao u prethodnom useEffect-u, tako da ovde ne mora ponovo
    }
  }, [tenantNameFromId, user, selectedTenantId, isCustomerUser]);
  
  // Funkcija koja proverava da li treba prikazati logo tenanta
  const shouldShowTenantLogo = () => {
    return (isEmployeeUser || isTenantAdminUser || isCustomerUser) && tenantLogoUrl;
  };

  // Ažuriraj ime tenanta na osnovu lokacije
  useEffect(() => {
    const isCustomerCheck = isCustomerUser;
    
    console.log('📍 Layout useEffect (location):', {
      pathname: location.pathname,
      userType: user?.userType,
      tenantConnectionsLength: user?.tenantConnections?.length,
      tenantNameFromId,
      currentTenantName,
      isCustomerCheck
    });
    
    if (location.pathname === '/homepage') {
      // Ukloni ime tenanta kada smo na homepage-u
      console.log('🏠 Clearing tenant name (on homepage)');
      setCurrentTenantName("");
    } else if (isCustomerCheck) {
      // Vrati ime tenanta kada nismo na homepage-u
      if (tenantNameFromId) {
        console.log('🔄 Setting tenant name from fetched data:', tenantNameFromId);
        setCurrentTenantName(tenantNameFromId);
      } else if (user?.tenantConnections?.length === 1 && !user?.tenantId) {
        const tenantConnection = user.tenantConnections[0];
        const tenantName = tenantConnection?.tenantId?.name || 'Tenant';
        console.log('📌 Setting tenant name from single connection (fallback):', tenantName);
        setCurrentTenantName(tenantName);
      }
    }
  }, [location.pathname, user, tenantNameFromId, currentTenantName, selectedTenantId, isCustomerUser]);

  const resolvedTenantName = useMemo(() => {
    if (!user) return '';

    if (isCustomerUser && !isTenantSpecificMode && location.pathname === '/homepage') {
      return '';
    }

    if (tenantNameFromId) return tenantNameFromId;
    if (currentTenantName) return currentTenantName;

    if (typeof user?.tenantId === 'object' && user?.tenantId?.name) {
      return user.tenantId.name;
    }

    if (user?.tenantConnections?.length) {
      const matchedConnection = user.tenantConnections.find((tc) => {
        if (!tc?.tenantId) return false;
        const connectionId = tc.tenantId?._id || tc.tenantId;
        return user?.tenantId ? connectionId === user.tenantId : tc.approved;
      }) || user.tenantConnections[0];

      if (matchedConnection?.tenantId?.name) {
        return matchedConnection.tenantId.name;
      }
    }

    if (tenantSubdomain) {
      const formatted = tenantSubdomain.replace(/[-_]/g, ' ');
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    }

    return '';
  }, [
    user,
    tenantNameFromId,
    currentTenantName,
    tenantSubdomain,
    isCustomerUser,
    isTenantSpecificMode,
    location.pathname
  ]);

  const shouldShowTenantHeader =
    Boolean(resolvedTenantName) &&
    (isCustomerUser || isTenantAdminUser || isEmployeeUser || isTenantSpecificMode);

  const handleCollapseToggle = () => setCollapsed((prev) => !prev);
  const handleMobileToggle = () => setMobileOpen((prev) => !prev);
  const handleRightSidebarToggle = () => setRightSidebarOpen((prev) => !prev);

  // Funkcija za dobijanje naslova na osnovu trenutne stranice
  const getCurrentPageTitle = () => {
    const path = location.pathname;
    
    // Specijalan slučaj za homepage
    if (path === '/homepage') {
      return 'Zakazuj';
    }
    
    const menuItems = getMenuItems();
    
    // Pronađi trenutnu stranicu u menu items
    const findPageTitle = (items) => {
      for (const item of items) {
        if (item.path === path) {
          return item.text;
        }
        if (item.subItems) {
          for (const subItem of item.subItems) {
            if (subItem.path === path) {
              return subItem.text;
            }
          }
        }
      }
      return null;
    };
    
    const pageTitle = findPageTitle(menuItems);
    return pageTitle;
  };

  const getMenuItems = () => {
    if (!user) return [];
    
    // Use helper functions that check tenantConnections
    const isSuperAdminUser = isSuperAdmin(user);
    const isTenantAdminUser = isTenantAdmin(user, selectedTenantId);
    const isEmployeeUser = isEmployee(user, selectedTenantId);
    const isCustomerUser = isCustomer(user, selectedTenantId);

    const baseItems = [
      // {
      //   text: "Kontrolna tabla",
      //   icon: <DashboardIcon />,
      //   page: "dashboard",
      //   path: "/dashboard",
      // },
      // {
      //   text: "Današnji raspored",
      //   icon: <LiveDashboardIcon />,
      //   page: "live-dashboard",
      //   path: "/live-dashboard",
      // },
    ];

    // Današnji raspored samo za tenant admina i zaposlenog
    const todayScheduleItem = {
      text: "Današnji raspored",
      icon: <LiveDashboardIcon />,
      page: "live-dashboard",
      path: "/live-dashboard",
    };

    if (isSuperAdminUser) {
      return [
        ...baseItems,
        // Današnji raspored nije dostupan za super admina
        {
          text: "Tenanti",
          icon: <BusinessIcon />,
          page: "tenants",
          path: "/tenants",
        },
        {
          text: "Korisnici",
          icon: <PeopleIcon />,
          page: "users",
          path: "/users",
        },
        {
          text: "Zaposleni",
          icon: <WorkIcon />,
          page: "employees",
          path: "/employees",
        },
        {
          text: "Termini",
          icon: <ScheduleIcon />,
          page: "appointments",
          path: "/appointments",
        },
        {
          text: "Kalendar",
          icon: <CalendarIcon />,
          page: "calendar",
          path: "/calendar",
        },
        {
          text: "Neradno vreme",
          icon: <EventBusyIcon />,
          page: "exceptions",
          path: "/exceptions",
        },
        {
          text: "Usluge",
          icon: <BuildIcon />,
          page: "services",
          path: "/services",
          subItems: [
            {
              text: "Lista Usluga",
              page: "services",
              path: "/services",
            },
            {
              text: "Kategorije",
              page: "categories",
              path: "/categories",
            },
          ],
        },
        {
          text: "Podešavanja",
          icon: <SettingsIcon />,
          page: "settings",
          path: "/settings",
          subItems: [
            {
              text: "Lookup Tabele",
              page: "lookup-tables",
              path: "/lookup-tables",
            },
          ],
        },
          {
            text: "Politika Privatnosti",
            icon: <PolicyIcon />,
            page: "privacy",
            path: "/privacy",
          },
          {
            text: "Zahtevi za saradnju",
            icon: <MailOutlineIcon />,
            page: "coop-requests",
            path: "/coop-requests",
          },
        // {
        //   text: "Test",
        //   icon: <ScienceIcon />,
        //   page: "test",
        //   path: "/test",
        // },
      ];
    }
    if (isTenantAdminUser) {
      const tenantAdminItems = [
        ...baseItems,
        todayScheduleItem,
        {
          text: "Statistika",
          icon: <BarChartIcon />,
          page: "statistics",
          path: "/statistics",
        },
        {
          text: "Korisnici",
          icon: <PeopleIcon />,
          page: "users",
          path: "/users",
        },
      ];

      // Dodaj stavku za pending users samo ako je approvalOfExternalUserRegistration omogućen
      if (tenantSettings?.approvalOfExternalUserRegistration) {
        tenantAdminItems.push({
          text: "Korisnici koji čekaju na registraciju",
          icon: <HourglassEmptyIcon />,
          page: "pending-users",
          path: "/pending-users",
        });
      }

      tenantAdminItems.push(
        {
          text: "Termini",
          icon: <ScheduleIcon />,
          page: "appointments",
          path: "/appointments",
        },
        {
          text: "Kalendar",
          icon: <CalendarIcon />,
          page: "calendar",
          path: "/calendar",
        },
        {
          text: "Neradno vreme",
          icon: <EventBusyIcon />,
          page: "exceptions",
          path: "/exceptions",
        },
        {
          text: "Usluge",
          icon: <BuildIcon />,
          page: "services",
          path: "/services",
          subItems: [
            {
              text: "Lista Usluga",
              page: "services",
              path: "/services",
            },
            {
              text: "Kategorije",
              page: "categories",
              path: "/categories",
            },
          ],
        },
        {
          text: "Podešavanja radnje",
          icon: <SettingsIcon />,
          page: "tenant-settings",
          path: "/tenant-settings",
        },
        {
          text: "Zaposleni",
          icon: <WorkIcon />,
          page: "employees",
          path: "/employees",
        },
        {
          text: "Politika Privatnosti",
          icon: <PolicyIcon />,
          page: "privacy",
          path: "/privacy",
        },
      );

      return tenantAdminItems;
    }
    if (isEmployeeUser) {
      return [
        ...baseItems,
        todayScheduleItem,
        {
          text: "Termini",
          icon: <ScheduleIcon />,
          page: "appointments",
          path: "/appointments",
        },
        {
          text: "Kalendar",
          icon: <CalendarIcon />,
          page: "calendar",
          path: "/calendar",
        },
        {
          text: "Politika Privatnosti",
          icon: <PolicyIcon />,
          page: "privacy",
          path: "/privacy",
        },
      ];
    }
    
    // Za customer korisnike
    if (isCustomerUser) {
      const customerItems = [
        ...baseItems,
        {
          text: "Poslednji i Naredni Termin",
          icon: <ScheduleIcon />,
          page: "lastAndNextAppointments",
          path: "/last-and-next-appointment",
        },
        {
          text: "Termini",
          icon: <ScheduleIcon />,
          page: "appointments",
          path: "/appointments",
        },
        {
          text: "Kalendar",
          icon: <CalendarIcon />,
          page: "calendar",
          path: "/calendar",
        },
        {
          text: "Politika Privatnosti",
          icon: <PolicyIcon />,
          page: "privacy",
          path: "/privacy",
        },
      ];

      // Prikaži "Katalog radnji" samo ako nije u tenant-specific modu I ako korisnik ima više od 1 tenantConnection
      if (!isTenantSpecificMode && user?.tenantConnections?.length > 1) {
        customerItems.push({
          text: "Katalog radnji",
          icon: <MoreHorizIcon />,
          page: "homepage",
          path: "/homepage",
        });
      }

      return customerItems;
    }
    
    return baseItems;
  };

  const menuItems = getMenuItems();

  const handleMenuClick = (page, path, hasSubItems = false) => {
    if (hasSubItems) {
      // Toggle expanded state for menus with sub-items
      setExpandedMenus((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(page)) {
          newSet.delete(page);
        } else {
          newSet.add(page);
        }
        return newSet;
      });
    } else {
      // Navigate to page for regular menu items
      dispatch(setCurrentPage(page));
      navigate(path);
    }
  };
  const handleLogout = () => {
    console.log('🔓 Logout - User email:', user?.email);
    console.log('🔓 Logout - User object:', user);
    
    // Proveri da li je korisnik u demo modu pre logout-a
    const wasInDemoMode = isDemoMode();
    
    dispatch(logout());
    
    // Ako je bio u demo modu, redirect-uj na demo landing stranicu
    if (wasInDemoMode) {
      navigate("/demo");
    } else {
      navigate("/login");
    }
  };

  // Account menu handlers
  const handleAccountMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileSettings = () => {
    // Get current user data from localStorage to ensure we have the latest
    const currentUser = authService.getUser();
    // Reset form with current user data
    setProfileForm({
      firstName: currentUser?.firstName || "",
      lastName: currentUser?.lastName || "",
      email: currentUser?.email || "",
      phone: currentUser?.phone || "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setProfileModalOpen(true);
    handleAccountMenuClose();
  };

  const handleProfileClose = () => {
    setProfileModalOpen(false);
    setProfileForm({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phone: user?.phone || "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const handleProfileSave = async () => {
    try {
      // Validate password if provided
      if (profileForm.newPassword || profileForm.currentPassword) {
        if (!profileForm.currentPassword) {
          toast.error("Morate uneti trenutnu lozinku");
          return;
        }
        if (!profileForm.newPassword) {
          toast.error("Morate uneti novu lozinku");
          return;
        }
        if (profileForm.newPassword !== profileForm.confirmPassword) {
          toast.error("Nove lozinke se ne poklapaju");
          return;
        }
      }

      const updateData = {
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        email: profileForm.email,
        phone: profileForm.phone,
      };

      // Update profile
      await authService.updateProfile(updateData);
      
      // If password is being changed, also call changePassword API
      if (profileForm.currentPassword && profileForm.newPassword) {
        await authService.changePassword(profileForm.currentPassword, profileForm.newPassword);
      }
      
      // Update user in localStorage and Redux
      const updatedUser = { ...user, ...updateData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Dispatch Redux action to update user in state
      dispatch(updateUser(updateData));
      
      toast.success("Profil je uspešno ažuriran!");
      handleProfileClose();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Greška pri ažuriranju profila');
    }
  };

  // Notification handlers
  const handleNotificationMenuOpen = async (event) => {
    setNotificationAnchor(event.currentTarget);
    await fetchNotifications();
    await fetchUnreadCount();
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchor(null);
  };

  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const response = await notificationService.getNotifications({
        tenantId: shouldFilterNotifications ? activeTenantId : undefined,
      });
      console.log('Notifications response:', response);

      // Handle different response structures
      let fetchedNotifications = [];
      if (Array.isArray(response)) {
        fetchedNotifications = response;
      } else if (response.notifications) {
        fetchedNotifications = response.notifications;
      } else if (response.data && Array.isArray(response.data)) {
        fetchedNotifications = response.data;
      }

      console.log('Parsed notifications:', fetchedNotifications);
      setAllNotifications(fetchedNotifications);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationService.getUnreadCount(
        shouldFilterNotifications ? activeTenantId : undefined
      );
      console.log('Unread count response:', response);
      // Try different possible response structures
      setUnreadCount(response.count || response.unreadCount || response.data?.count || response.data?.unreadCount || 0);
    } catch (err) {
      console.error('Error fetching unread count:', err);
      // If the endpoint doesn't exist, try fetching all notifications and counting unread ones
      try {
        const notificationsResponse = await notificationService.getNotifications({
          tenantId: shouldFilterNotifications ? activeTenantId : undefined,
        });
        const fallbackList = notificationsResponse.notifications || notificationsResponse.data || [];
        const scopedList = (!shouldFilterNotifications || !activeTenantId)
          ? fallbackList
          : fallbackList.filter((n) => extractNotificationTenantId(n) === activeTenantId);
        const unreadNotifications = scopedList.filter(n => !n.isRead);
        setAllNotifications(fallbackList);
        setUnreadCount(unreadNotifications.length);
      } catch (err2) {
        console.error('Error fetching notifications for count:', err2);
        setUnreadCount(0);
      }
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      console.log('Marking notification as read:', id);
      
      // Update UI immediately
      setAllNotifications((prev) =>
        prev.map((n) =>
          (n._id === id || n.id === id) ? { ...n, isRead: true } : n
        )
      );
      setUnreadCount(Math.max(0, unreadCount - 1));
      
      // Try to update backend
      try {
        await notificationService.markAsRead(id);
        console.log('Successfully marked as read on backend');
      } catch (apiErr) {
        console.log('Backend mark as read failed:', apiErr);
        // Keep the UI update even if backend fails
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setAllNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
      toast.success('Sva obaveštenja su označena kao pročitana');
    } catch (err) {
      console.error('Error marking all as read:', err);
      toast.error(err.response?.data?.message || err.message || 'Greška pri označavanju obaveštenja');
    }
  };

  const handleApproveRequest = async (notification) => {
    try {
      // Call approve API endpoint
      await tenantUsersService.approveUser(notification.requestedBy, notification.tenantId);
      

      setUnreadCount(Math.max(0, unreadCount - 1));
      
      //toast.success('Zahtev za registraciju je odobren');
    } catch (err) {
      console.error('Error approving request:', err);
      toast.error(err.response?.data?.message || err.message || 'Greška pri odobravanju zahteva');
    }
  };

  const handleRejectRequest = async (notification) => {
    try {
      // Call reject API endpoint
      await tenantUsersService.rejectUser(notification.requestedBy, notification.tenantId);
      
      setUnreadCount(Math.max(0, unreadCount - 1));
      
      //toast.success('Zahtev za registraciju je odbačen');
    } catch (err) {
      console.error('Error rejecting request:', err);
      toast.error(err.response?.data?.message || err.message || 'Greška pri odbacivanju zahteva');
    }
  };

  const handleConfirmAppointment = async (notification) => {
    try {
      // Call confirm appointment API endpoint
      await appointmentService.confirmAppointment(notification.collectionId);
      
      setUnreadCount(Math.max(0, unreadCount - 1));
      
    } catch (err) {
      console.error('Error confirming appointment:', err);
      toast.error(err.response?.data?.message || err.message || 'Greška pri potvrđivanju termina');
    }
  };

  const handleRejectAppointment = async (notification) => {
    try {
      // Call reject appointment API endpoint
      await appointmentService.cancelAppointment(
                notification.collectionId,
                "Odbijen od strane admina"
              );
      
      setUnreadCount(Math.max(0, unreadCount - 1));
      
    } catch (err) {
      console.error('Error rejecting appointment:', err);
      toast.error(err.response?.data?.message || err.message || 'Greška pri odbacivanju termina');
    }
  };

  // Fetch notifications and unread count on mount / tenant change
  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    fetchUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, shouldFilterNotifications, activeTenantId]);

  // Periodic notification check every minute
  useEffect(() => {
    if (!user) return;

    const checkNotifications = async () => {
      try {
        const response = await notificationService.getUnreadCount(
          shouldFilterNotifications ? activeTenantId : undefined
        );
        const newUnreadCount = response.count || response.unreadCount || response.data?.count || response.data?.unreadCount || 0;
        
        setUnreadCount((prev) => (prev === newUnreadCount ? prev : newUnreadCount));
      } catch (err) {
        console.error('Error checking for new notifications:', err);
      }
    };

    const notificationInterval = setInterval(checkNotifications, 60000);
    checkNotifications();

    return () => {
      clearInterval(notificationInterval);
    };
  }, [user, shouldFilterNotifications, activeTenantId]);

  // Header minimization on scroll (mobile only)
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Minimize header when scrolling down, show when scrolling up
      if (currentScrollY > 100) {
        if (currentScrollY > lastScrollY && currentScrollY > 150) {
          // Scrolling down - minimize
          setHeaderMinimized(true);
        } else if (currentScrollY < lastScrollY) {
          // Scrolling up - show
          setHeaderMinimized(false);
        }
      } else {
        // Near top - always show
        setHeaderMinimized(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, lastScrollY]);

  const drawerWidth = collapsed ? collapsedWidth : expandedWidth;

  // Sakrij sidebar na homepage stranici za customer korisnike (ili one bez userType)
  // Takođe sakrij sidebar i navbar u fullscreen modu na live-dashboard stranici
  const isCustomerCheck = isCustomerUser;
  const isLiveDashboard = location.pathname === '/live-dashboard';
  // Sakrij sidebar i navbar samo ako je EKSPLICITNO u fullscreen modu (isFullscreen === true)
  const shouldShowSidebar = !(location.pathname === '/homepage' && isCustomerCheck) && !(isFullscreen === true && isLiveDashboard);
  const shouldShowNavbar = !(isFullscreen === true && isLiveDashboard);

  const drawer = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: isDarkMode 
          ? "linear-gradient(180deg, #0F1431 0%, #171B46 100%)"
          : "linear-gradient(180deg, #E2E8F0 0%, #CBD5E1 50%, #94A3B8 100%)",
        color: isDarkMode ? "white" : "black",
        transition: "width 0.3s",
      }}
    >
      <Box sx={{ textAlign: "center", py: 2 }}>
        <Box
          component="img"
          src={Logo}
          alt="Zakazuj"
          sx={{
            height: (collapsed && !isMobile) ? 40 : 80,
            transition: "all 0.3s",
            objectFit: "contain",
            filter: "drop-shadow(0 0 12px rgba(124,58,237,0.6))",
          }}
        />

        {(!collapsed || isMobile) && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {shouldShowTenantLogo() ? (
              <Box
                component="img"
                src={tenantLogoUrl}
                alt="Tenant Logo"
                sx={{
                  width: "100%",
                  maxWidth: 200,
                  height: "auto",
                  maxHeight: 120,
                  objectFit: "contain",
                  mb: 1,
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}
              />
            ) : (
              <>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.12)",
                    border: "2px solid rgba(255,255,255,0.25)",
                    width: 96,
                    height: 96,
                    mb: 1,
                    fontSize: "2rem",
                    fontWeight: 700,
                  }}
                  src={user?.avatarUrl || ""}
                >
                  {user?.email?.[0]?.toUpperCase() || "U"}
                </Avatar>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}
                >
                  {user?.email}
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>

      <List sx={{ flexGrow: 1 }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding sx={{ display: "block" }}>
              <Tooltip title={collapsed ? item.text : ""} placement="right">
                <ListItemButton
                  onClick={() =>
                    handleMenuClick(item.page, item.path, !!item.subItems)
                  }
                  selected={location.pathname === item.path && !item.subItems}
                  sx={{
                    minHeight: 48,
                    justifyContent: collapsed ? "center" : "initial",
                    px: 2.5,
                    "&.Mui-selected": {
                      background:
                        "linear-gradient(90deg, #3B82F6 0%, #1D4ED8 48%, #06B6D4 100%)",
                      color: isDarkMode ? "white" : "black",
                      "& .MuiListItemIcon-root": { color: isDarkMode ? "white" : "black" },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: collapsed ? "auto" : 2,
                      justifyContent: "center",
                      color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {(!collapsed || isMobile) && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexGrow: 1,
                      }}
                    >
                      <ListItemText 
                        primary={item.text} 
                        sx={{
                          "& .MuiTypography-root": {
                            color: isDarkMode ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.9)",
                          }
                        }}
                      />
                      {item.subItems && (
                        <Box
                          sx={{
                            transform: expandedMenus.has(item.page)
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.2s",
                          }}
                        >
                          ▼
                        </Box>
                      )}
                    </Box>
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>

            {/* Render sub-items if they exist and menu is expanded */}
            {(!collapsed || isMobile) &&
              item.subItems &&
              expandedMenus.has(item.page) &&
              item.subItems.map((subItem) => (
                <ListItem
                  key={subItem.text}
                  disablePadding
                  sx={{ display: "block" }}
                >
                  <ListItemButton
                    onClick={() => handleMenuClick(subItem.page, subItem.path)}
                    selected={location.pathname === subItem.path}
                    sx={{
                      minHeight: 40,
                      pl: 4,
                      "&.Mui-selected": {
                        background:
                          "linear-gradient(90deg, #3B82F6 0%, #1D4ED8 48%, #06B6D4 100%)",
                        color: isDarkMode ? "white" : "black",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          backgroundColor:
                            location.pathname === subItem.path
                              ? "white"
                              : "rgba(255,255,255,0.5)",
                          mr: 1.5,
                          flexShrink: 0,
                        }}
                      />
                      <ListItemText
                        primary={subItem.text}
                        sx={{
                          fontSize: "0.9rem",
                          "& .MuiTypography-root": { 
                            fontSize: "0.9rem",
                            color: isDarkMode ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                          },
                        }}
                      />
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CssBaseline />

      {shouldShowNavbar && (
      <AppBar
        position="fixed"
        sx={{
          width: isMobile ? '100%' : shouldShowSidebar ? `calc(100% - ${drawerWidth}px)` : '100%',
          ml: isMobile ? 0 : shouldShowSidebar ? `${drawerWidth}px` : 0,
          background: isDarkMode 
            ? "linear-gradient(90deg, #EC4899 0%, #8B5CF6 50%, #3B82F6 65%, #1E40AF 100%)"
            : "linear-gradient(90deg, #F8FAFC 0%, #F1F5F9 50%, #E2E8F0 65%, #CBD5E1 100%)",
          boxShadow: isDarkMode 
            ? "0 4px 20px rgba(0,0,0,0.3)" 
            : "0 4px 20px rgba(0,0,0,0.1)",
          transition: "width 0.3s, margin 0.3s, transform 0.3s ease-in-out",
          transform: isMobile && headerMinimized ? 'translateY(-100%)' : 'translateY(0)',
        }}
      >
        <Toolbar sx={{
          color: isDarkMode ? 'white' : '#1E293B',
          minHeight: isMobile && headerMinimized ? '48px !important' : '64px !important',
          transition: 'min-height 0.3s ease-in-out',
        }}>
          {/* Hamburger menu za mobilne uređaje */}
          {isMobile && shouldShowSidebar ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleMobileToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          ) : !isMobile && shouldShowSidebar ? (
            <IconButton
              color="inherit"
              aria-label="collapse drawer"
              edge="start"
              onClick={handleCollapseToggle}
              sx={{ mr: 2 }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          ) : null}
          {/* Naslov stranice - levo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" noWrap>
              {getCurrentPageTitle() || (user?.userType === "super_admin"
                  ? "Admin Panel"
                  : user?.userType === "tenant_admin" || user?.userType === "employee"
                  ? `${user?.tenantId?.name || "Tenant"} Admin`
                  : "Zakazuj Me")}
            </Typography>
            {isDemoMode() && (
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  backgroundColor: '#ff9800',
                  color: 'white',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                DEMO
              </Box>
            )}
          </Box>
          
          {/* Spacer za centriranje */}
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Ime tenanta - sredina */}
          {shouldShowTenantHeader && !isMobile && (
            <Typography variant="h5" noWrap sx={{ 
              color: isDarkMode ? 'white' : '#1E293B',
              fontWeight: 700,
              fontSize: '1.5rem',
              textShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.5)' : '0 1px 2px rgba(0,0,0,0.1)',
              mr: 2
            }}>
              {resolvedTenantName}
            </Typography>
          )}
          
          {/* Spacer za centriranje */}
          <Box sx={{ flexGrow: 1 }} />
          {/* Novi termin dugme - samo na appointments stranici i mobilnom */}
          {getCurrentPageTitle() === "Termini" && isMobile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                // Ovo će biti handle-ovano u Appointments komponenti
                window.dispatchEvent(new CustomEvent('openAppointmentDialog'));
              }}
              sx={{
                mr: 2,
                background: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 100%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(90deg, #2563EB 0%, #1E40AF 100%)',
                }
              }}
            >
              Novi
            </Button>
          )}
          
          {/* Dodaj neradno vreme dugme - samo na exceptions stranici i mobilnom */}
          {getCurrentPageTitle() === "Neradno vreme" && isMobile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                // Ovo će biti handle-ovano u Exceptions komponenti
                window.dispatchEvent(new CustomEvent('openExceptionDialog'));
              }}
              sx={{
                mr: 2,
                background: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 100%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(90deg, #2563EB 0%, #1E40AF 100%)',
                }
              }}
            >
              Novo
            </Button>
          )}
          
          {/* Dodaj uslugu dugme - samo na services stranici i mobilnom */}
          {getCurrentPageTitle() === "Usluge" && isMobile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={user?.userType === "super_admin" && !selectedTenantId}
              onClick={() => {
                // Ovo će biti handle-ovano u Services komponenti
                window.dispatchEvent(new CustomEvent('openServiceDialog'));
              }}
              sx={{
                mr: 2,
                background: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 100%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(90deg, #2563EB 0%, #1E40AF 100%)',
                },
                '&:disabled': {
                  background: 'rgba(156, 163, 175, 0.5)',
                  color: 'rgba(107, 114, 128, 0.7)',
                }
              }}
            >
              Nova
            </Button>
          )}
          
          {/* Dodaj kategoriju dugme - samo na categories stranici i mobilnom */}
          {getCurrentPageTitle() === "Kategorije" && isMobile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={user?.userType === "super_admin" && !selectedTenantId}
              onClick={() => {
                // Ovo će biti handle-ovano u Categories komponenti
                window.dispatchEvent(new CustomEvent('openCategoryDialog'));
              }}
              sx={{
                mr: 2,
                background: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 100%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(90deg, #2563EB 0%, #1E40AF 100%)',
                },
                '&:disabled': {
                  background: 'rgba(156, 163, 175, 0.5)',
                  color: 'rgba(107, 114, 128, 0.7)',
                }
              }}
            >
              Nova
            </Button>
          )}
          
          {/* Dodaj korisnika dugme - samo na users stranici i mobilnom */}
          {getCurrentPageTitle() === "Korisnici" && isMobile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                // Ovo će biti handle-ovano u Users komponenti
                window.dispatchEvent(new CustomEvent('openUserDialog'));
              }}
              sx={{
                mr: 2,
                background: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 100%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(90deg, #2563EB 0%, #1E40AF 100%)',
                }
              }}
            >
              Novi
            </Button>
          )}

          {/* Dodaj zaposlenog dugme - samo na employees stranici i mobilnom */}
          {getCurrentPageTitle() === "Zaposleni" && isMobile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                // Ovo će biti handle-ovano u Users komponenti
                window.dispatchEvent(new CustomEvent('openEmployeeDialog'));
              }}
              sx={{
                mr: 2,
                background: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 100%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(90deg, #2563EB 0%, #1E40AF 100%)',
                }
              }}
            >
              Novi
            </Button>
          )}
          
          {/* Dodaj tenant dugme - samo na tenants stranici i mobilnom */}
          {getCurrentPageTitle() === "Tenanti" && isMobile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                // Ovo će biti handle-ovano u Tenants komponenti
                window.dispatchEvent(new CustomEvent('openTenantDialog'));
              }}
              sx={{
                mr: 2,
                background: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 100%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(90deg, #2563EB 0%, #1E40AF 100%)',
                }
              }}
            >
              Novi
            </Button>
          )}
          
          {/* Mobilni navigacioni meni */}
          {isMobile ? (
            <IconButton
              color="inherit"
              onClick={handleRightSidebarToggle}
              sx={{
                background: isDarkMode 
                  ? 'rgba(255, 255, 255, 0.2)' 
                  : 'transparent',
                border: isDarkMode 
                  ? '1px solid rgba(255, 255, 255, 0.3)' 
                  : 'none',
                color: isDarkMode ? 'white' : '#1E293B',
                '&:hover': {
                  background: isDarkMode 
                    ? 'rgba(255, 255, 255, 0.3)' 
                    : 'rgba(0, 0, 0, 0.05)',
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <>
              {/* Email prikaz */}
              {user?.email && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mr: 2, 
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 41, 59, 0.9)',
                    fontWeight: 500
                  }}
                >
                  {user.email}
                </Typography>
              )}
              
              {/* Notification Bell */}
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <IconButton
                  onClick={handleNotificationMenuOpen}
                  sx={{
                    color: isDarkMode ? 'white' : '#1E293B',
                    '&:hover': {
                      backgroundColor: isDarkMode 
                        ? 'rgba(255, 255, 255, 0.1)' 
                        : 'rgba(0, 0, 0, 0.05)',
                    }
                  }}
                >
                  <NotificationsIcon />
                </IconButton>
              </Badge>
              
              <IconButton
                onClick={handleAccountMenuOpen}
                sx={{
                  color: isDarkMode ? 'white' : '#1E293B',
                  '&:hover': {
                    backgroundColor: isDarkMode 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <AccountCircleIcon />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleAccountMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'account-button',
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={handleProfileSettings}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Podešavanje profila
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>

              {/* Notification Menu */}
              <NotificationMenu
                anchorEl={notificationAnchor}
                open={notificationMenuOpen}
                onClose={handleNotificationMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  sx: {
                    width: 700,
                    maxHeight: 600,
                    background: isDarkMode 
                      ? 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)'
                      : '#ffffff',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                    boxShadow: isDarkMode 
                      ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                      : '0 8px 32px rgba(0, 0, 0, 0.1)',
                  }
                }}
              >
                <Box sx={{ p: 2, borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <MuiTypography variant="h6" sx={{ color: isDarkMode ? 'white' : '#1E293B', fontWeight: 600 }}>
                      Obaveštenja
                    </MuiTypography>
                    {unreadCount > 0 && (
                      <Button
                        size="small"
                        onClick={handleMarkAllAsRead}
                        sx={{
                          color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                          fontSize: '0.75rem'
                        }}
                      >
                        Označi sve kao pročitano
                      </Button>
                    )}
                  </Box>
                </Box>
                <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                  {loadingNotifications ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress size={30} />
                    </Box>
                  ) : filteredNotifications.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <MuiTypography variant="body2" sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)' }}>
                        Nema obaveštenja
                      </MuiTypography>
                    </Box>
                  ) : (
                    <MenuList>
                      {filteredNotifications.map((notification, index) => (
                        <React.Fragment key={notification._id || notification.id || index}>
                          <NotificationItem
                            notification={notification}
                            index={index}
                            totalCount={filteredNotifications.length}
                            isDarkMode={isDarkMode}
                            onMarkAsRead={handleMarkAsRead}
                            onApproveRequest={handleApproveRequest}
                            onRejectRequest={handleRejectRequest}
                            onConfirmAppointment={handleConfirmAppointment}
                            onRejectAppointment={handleRejectAppointment}
                            onRefreshNotifications={fetchNotifications}
                          />
                          {index < filteredNotifications.length - 1 && (
                            <Divider sx={{ my: 0.5, bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' }} />
                          )}
                        </React.Fragment>
                      ))}
                    </MenuList>
                  )}
                </Box>
              </NotificationMenu>
            </>
          )}
        </Toolbar>
      </AppBar>
      )}

      {/* Sidebar - prikaži samo ako shouldShowSidebar je true */}
      {shouldShowSidebar && (
        <Box component="nav" sx={{ width: isMobile ? 0 : drawerWidth, flexShrink: 0 }}>
          {/* Desktop drawer */}
          {!isMobile && (
            <Drawer
              variant="permanent"
              sx={{
                "& .MuiDrawer-paper": {
                  width: drawerWidth,
                  transition: "width 0.3s",
                  overflowX: "hidden",
                  border: "none",
                },
              }}
              open
            >
              {drawer}
            </Drawer>
          )}
          
          {/* Mobile drawer */}
          {isMobile && (
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleMobileToggle}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
              sx={{
                "& .MuiDrawer-paper": {
                  width: expandedWidth,
                  overflowX: "hidden",
                  border: "none",
                },
                zIndex: 1400, // Veći od footer-a (1300)
              }}
            >
              {drawer}
            </Drawer>
          )}
        </Box>
      )}

      {/* Desni mini sidebar za mobilni */}
      {isMobile && (
        <Drawer
          anchor="right"
          variant="temporary"
          open={rightSidebarOpen}
          onClose={handleRightSidebarToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            "& .MuiDrawer-paper": {
              width: 280,
              height: 'auto',
              top: 64, // Počni ispod AppBar-a
              bottom: 'auto',
              background: isDarkMode 
                ? "linear-gradient(180deg, #1E293B 0%, #0F172A 100%)"
                : "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
              border: "none",
              boxShadow: isDarkMode 
                ? "0 8px 32px rgba(0,0,0,0.3)" 
                : "0 8px 32px rgba(0,0,0,0.1)",
              borderRadius: '0 0 8px 8px',
            },
          }}
        >
          <Box sx={{ 
            p: 3, 
            display: 'flex', 
            flexDirection: 'column',
            gap: 2
          }}>
            {/* Header */}
            <Typography 
              variant="h6" 
              sx={{ 
                color: isDarkMode ? 'white' : '#1E293B',
                fontWeight: 600
              }}
            >
              Meni
            </Typography>
            
            {/* Theme toggle - sakriveno na mobilnim */}
            {!isMobile && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                onClick={toggleTheme}
                sx={{
                  justifyContent: 'flex-start',
                  color: isDarkMode ? 'white' : '#1E293B',
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                  '&:hover': {
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  }
                }}
              >
                {isDarkMode ? "Light Mode" : "Dark Mode"}
              </Button>
            )}
            
            {/* Logout */}
            <Button
              fullWidth
              variant="contained"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                justifyContent: 'flex-start',
                background: 'linear-gradient(90deg, #EF4444 0%, #DC2626 100%)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)',
                }
              }}
            >
              Logout
            </Button>
          </Box>
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: (isFullscreen && isLiveDashboard) ? 0 : 3,
          width: isMobile ? '100%' : (shouldShowSidebar && shouldShowNavbar) ? `calc(100% - ${drawerWidth}px)` : '100%',
          transition: "margin 0.3s, width 0.3s",
          background: isDarkMode 
            ? "radial-gradient(1200px 800px at 20% 30%, #1e2a78 0%, #0b0f2b 50%, #0a0d20 100%)"
            : "#ffffff",
        }}
      >
        {shouldShowNavbar && <Toolbar />}
        {children}
      </Box>

      {/* Profile Settings Modal */}
      <Dialog 
        open={profileModalOpen} 
        onClose={handleProfileClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: isDarkMode 
              ? 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)'
              : '#ffffff',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
            boxShadow: isDarkMode 
              ? '0 8px 32px rgba(0, 0, 0, 0.4)'
              : '0 8px 32px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: isDarkMode ? 'white' : '#1E293B',
          fontWeight: 600,
          borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          pb: 2
        }}>
          Podešavanje profila
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Ime"
              value={profileForm.firstName}
              onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : undefined }
              }}
              InputProps={{
                sx: {
                  color: isDarkMode ? 'white' : undefined,
                  '&::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : undefined },
                  '&:hover::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : undefined }
                }
              }}
            />
            <TextField
              fullWidth
              label="Prezime"
              value={profileForm.lastName}
              onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : undefined }
              }}
              InputProps={{
                sx: {
                  color: isDarkMode ? 'white' : undefined,
                  '&::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : undefined },
                  '&:hover::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : undefined }
                }
              }}
            />
            <TextField
              fullWidth
              label="Email"
              value={profileForm.email}
              onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : undefined }
              }}
              InputProps={{
                sx: {
                  color: isDarkMode ? 'white' : undefined,
                  '&::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : undefined },
                  '&:hover::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : undefined }
                }
              }}
            />
            <TextField
              fullWidth
              label="Telefon"
              value={profileForm.phone}
              onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : undefined }
              }}
              InputProps={{
                sx: {
                  color: isDarkMode ? 'white' : undefined,
                  '&::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : undefined },
                  '&:hover::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : undefined }
                }
              }}
            />
            <TextField
              fullWidth
              label="Trenutna lozinka"
              type="password"
              value={profileForm.currentPassword}
              onChange={(e) => setProfileForm({ ...profileForm, currentPassword: e.target.value })}
              helperText="Unesite trenutnu lozinku da biste promenili lozinku"
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : undefined }
              }}
              InputProps={{
                sx: {
                  color: isDarkMode ? 'white' : undefined,
                  '&::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : undefined },
                  '&:hover::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : undefined }
                }
              }}
              FormHelperTextProps={{
                sx: { color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : undefined }
              }}
            />
            {profileForm.currentPassword && (
              <>
                <TextField
                  fullWidth
                  label="Nova lozinka"
                  type="password"
                  value={profileForm.newPassword}
                  onChange={(e) => setProfileForm({ ...profileForm, newPassword: e.target.value })}
                  sx={{ mb: 2 }}
                  InputLabelProps={{
                    sx: { color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : undefined }
                  }}
                  InputProps={{
                    sx: {
                      color: isDarkMode ? 'white' : undefined,
                      '&::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : undefined },
                      '&:hover::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : undefined }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Potvrdite novu lozinku"
                  type="password"
                  value={profileForm.confirmPassword}
                  onChange={(e) => setProfileForm({ ...profileForm, confirmPassword: e.target.value })}
                  sx={{ mb: 2 }}
                  InputLabelProps={{
                    sx: { color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : undefined }
                  }}
                  InputProps={{
                    sx: {
                      color: isDarkMode ? 'white' : undefined,
                      '&::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : undefined },
                      '&:hover::before': { borderBottomColor: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : undefined }
                    }
                  }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          px: 3,
          py: 2
        }}>
          <Button 
            onClick={handleProfileClose}
            sx={{
              color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              }
            }}
          >
            Otkaži
          </Button>
          <Button 
            onClick={handleProfileSave} 
            variant="contained"
            sx={{
              background: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(90deg, #2563EB 0%, #1E40AF 100%)',
              }
            }}
          >
            Sačuvaj
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout;
